import { useState, useCallback, useEffect, useRef } from 'react';
import { API_CONFIG, getAuthHeadersWithoutCredentials } from '../constants/api';
import { ProjectFormData, FileUploadState } from '../types/project';
import React from 'react';
import { useSoundworldsContext } from '../contexts/SoundworldsContext';
import toast from 'react-hot-toast';
// import { useWebSocket } from './useWebSocket';

export const useProjectCreation = () => {
    const { soundworlds } = useSoundworldsContext();
    // const { isConnected: isWebSocketConnected, connect } = useWebSocket();

    const [fileUploadState, setFileUploadState] = useState<FileUploadState>({
        selectedFile: null,
        acknowledged: false,
        loading: false,
        projectId: null,
    });

    const [formData, setFormData] = useState<ProjectFormData>({
        projectName: '',
        description: '',
        soundworld: '',
        loopMode: false,
        sectionType: 'verse',
    });

    const audioReadyCallbackRef = useRef<(() => void) | null>(null);


    useEffect(() => {
        if (soundworlds.length > 0 && !formData.soundworld) {
            const firstSoundworld = soundworlds[0];
            setFormData(prev => ({
                ...prev,
                soundworld: firstSoundworld.id
            }));
        }
    }, [soundworlds, formData.soundworld, setFormData]);



    const showError = useCallback((msg: string) => {
        toast.error(msg);
    }, []);

    const isValidJsonFile = useCallback((file: File) => {
        return file && file.type === 'application/json';
    }, []);

    const isValidAudioFile = useCallback((file: File) => {
        return file && (file.type.startsWith('audio/') ||
            file.name.toLowerCase().endsWith('.mp3') ||
            file.name.toLowerCase().endsWith('.wav') ||
            file.name.toLowerCase().endsWith('.m4a') ||
            file.name.toLowerCase().endsWith('.aac'));
    }, []);

    const createProject = useCallback(async (tags: string, renderMode?: string) => {
        if (!formData.projectName.trim()) {
            showError("Please enter a project name.");
            return null;
        }

        try {
            const params = new URLSearchParams({
                name: formData.projectName
            });

            if (renderMode) {
                params.append('render_mode', renderMode);
            }

            const res = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.NEW_PROJECT}?${params.toString()}`,
                {
                    method: 'GET',
                    headers: getAuthHeadersWithoutCredentials(),
                }
            );

            if (!res.ok) {
                throw new Error("Failed to create project");
            }

            const newProject = await res.json();

            // Set project tags after creation
            if (tags) {
                try {
                    await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.SET_PROJECT_TAGS}`, {
                        method: 'POST',
                        headers: getAuthHeadersWithoutCredentials(),
                        body: JSON.stringify({
                            project_id: newProject.project_id,
                            tags: [tags]
                        })
                    });
                } catch (tagError) {
                    console.error("Failed to set project tags:", tagError);
                }
            }

            return newProject.project_id;
        } catch (err) {
            showError("Failed to create project.");
            return null;
        }
    }, [formData.projectName, showError]);

    const uploadFile = useCallback(async (file: File, projectId: string, role: string = 'data') => {
        try {
            const formData = new FormData();
            formData.append('file', file);

            const headers = getAuthHeadersWithoutCredentials();
            // Remove Content-Type header to let browser set it automatically for FormData
            delete headers['Content-Type'];

            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.UPLOAD}?project_id=${projectId}&role=${role}`, {
                method: 'POST',
                headers: headers,
                body: formData,
            });

            if (!response.ok) {
                throw new Error(`Upload failed: ${response.status}`);
            }

            const result = await response.json();
            return result;
        } catch (error) {
            throw error;
        }
    }, []);

    const deleteFile = useCallback(async (projectId: string, role: string) => {
        try {
            const res = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.DELETE_FILE}?project_id=${projectId}&role=${role}`, {
                method: 'DELETE',
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                throw new Error(`Failed to delete ${role} file`);
            }

            return await res.json();
        } catch (err) {
            showError(`Failed to delete ${role} file.`);
            return false;
        }
    }, [showError]);

    const executeProject = useCallback(async (
        projectId: string,
        soundworldId: string,
        loopMode: boolean = false,
        sectionType: string = 'verse',
        onAudioReady?: () => void
    ) => {
        setFileUploadState(prev => ({ ...prev, loading: true }));

        // if (!isWebSocketConnected) {
        //     await connect();
        //     // showError('WebSocket connection is not available. Please check your connection and try again.');
        //     // setFileUploadState(prev => ({ ...prev, loading: false }));
        //     // return null;
        // }

        // Debug: check parameters
        if (typeof soundworldId !== 'string') {
            showError('Invalid soundworld parameter');
            setFileUploadState(prev => ({ ...prev, loading: false }));
            return null;
        }

        if (!projectId || typeof projectId !== 'string') {
            showError('Invalid project ID');
            setFileUploadState(prev => ({ ...prev, loading: false }));
            return null;
        }

        try {
            const params = new URLSearchParams({
                project_id: projectId,
                soundworld: soundworldId
            });

            const sessionId = localStorage.getItem('session_id');
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            // Only add section_type_override if loopMode is true
            if (loopMode) {
                params.append('section_type_override', sectionType);
            }

            const url = `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.EXECUTE}?${params.toString()}`;

            const res = await fetch(url, {
                method: 'GET',
                headers: getAuthHeadersWithoutCredentials(),
            });

            if (!res.ok) {
                throw new Error('Failed to execute project');
            }

            const result = await res.json();

            if (result === "OK" || result.job_id) {
                if (onAudioReady) {
                    audioReadyCallbackRef.current = onAudioReady;
                }

                return result.job_id || 'executed';
            } else {
                showError('Error during execution: ' + JSON.stringify(result));
                setFileUploadState(prev => ({ ...prev, loading: false }));
                return null;
            }
        } catch (err) {
            showError("Something went wrong during generation.");
            setFileUploadState(prev => ({ ...prev, loading: false }));
            return null;
        }
    }, [showError]);

    const generateComposition = useCallback(async (projectId: string, soundworldId: string) => {
        try {
            // if (!isWebSocketConnected) {
            //     showError('WebSocket connection is not available. Please check your connection and try again.');
            //     return null;
            // }
            const res = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.GENERATE}?project_id=${projectId}&soundworld_id=${soundworldId}`,
                {
                    method: 'GET',
                    headers: getAuthHeadersWithoutCredentials(),
                }
            );

            if (!res.ok) {
                throw new Error('Failed to generate composition');
            }

            return await res.json();
        } catch (err) {
            showError("Failed to generate composition.");
            return null;
        }
    }, [showError]);

    const renderAudio = useCallback(async (projectId: string) => {
        try {
            // if (!isWebSocketConnected) {
            //     showError('WebSocket connection is not available. Please check your connection and try again.');
            //     return null;
            // }
            const res = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RENDER}?project_id=${projectId}`,
                {
                    method: 'GET',
                    headers: getAuthHeadersWithoutCredentials(),
                }
            );

            if (!res.ok) {
                throw new Error('Failed to render audio');
            }

            return await res.json();
        } catch (err) {
            showError("Failed to render audio.");
            return null;
        }
    }, [showError]);

    const renderStems = useCallback(async (projectId: string) => {
        try {
            // if (!isWebSocketConnected) {
            //     showError('WebSocket connection is not available. Please check your connection and try again.');
            //     return null;
            // }
            const params = new URLSearchParams({
                project_id: projectId
            });

            const sessionId = localStorage.getItem('session_id');
            if (sessionId) {
                params.append('session_id', sessionId);
            }

            const res = await fetch(
                `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.RENDER_STEMS}?${params.toString()}`,
                {
                    method: 'GET',
                    headers: getAuthHeadersWithoutCredentials(),
                }
            );

            if (!res.ok) {
                throw new Error('Failed to render stems');
            }

            return await res.json();
        } catch (err) {
            showError("Failed to render stems.");
            return null;
        }
    }, [showError]);







    useEffect(() => {
        const handleAudioReady = (event: CustomEvent) => {
            setFileUploadState(prev => ({ ...prev, loading: false }));

            if (audioReadyCallbackRef.current) {
                try {
                    audioReadyCallbackRef.current();
                } catch (error) {
                }
                audioReadyCallbackRef.current = null;
            }
        };

        const handleStemsReady = (event: CustomEvent) => {
            setFileUploadState(prev => ({ ...prev, loading: false }));
        };

        const handleSoundworldStemsReady = (event: CustomEvent) => {
            setFileUploadState(prev => ({ ...prev, loading: false }));
        };

        const handleJobError = (event: CustomEvent) => {
            setFileUploadState(prev => ({ ...prev, loading: false }));
            showError(`Job failed: ${event.detail.message}`);
        };

        window.addEventListener('audioReady', handleAudioReady as EventListener);
        window.addEventListener('stemsReady', handleStemsReady as EventListener);
        window.addEventListener('soundworldStemsReady', handleSoundworldStemsReady as EventListener);
        window.addEventListener('jobError', handleJobError as EventListener);

        return () => {
            window.removeEventListener('audioReady', handleAudioReady as EventListener);
            window.removeEventListener('stemsReady', handleStemsReady as EventListener);
            window.removeEventListener('soundworldStemsReady', handleSoundworldStemsReady as EventListener);
            window.removeEventListener('jobError', handleJobError as EventListener);
        };
    }, [showError]);



    return {
        fileUploadState,
        setFileUploadState,
        formData,
        setFormData,
        showError,
        isValidJsonFile,
        isValidAudioFile,
        createProject,
        uploadFile,
        deleteFile,
        executeProject,
        generateComposition,
        renderAudio,
        renderStems,
    };
};